import React, { useState, useEffect, useRef } from "react";
import { BotMessageSquare, X, Send } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = window.sessionStorage.getItem("medica-chat-messages");
      if (saved) return JSON.parse(saved);
    }
    return [
      {
        from: "bot",
        text: "Hi! I'm Medica AI. How can I assist you today?",
      },
    ];
  });
  const [loading, setLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const chatWindowRef = useRef(null);

  const normalizeAnswer = (ans) => {
    if (typeof ans === "string") return ans;
    if (ans == null) return "";
    if (typeof ans === "object") {
      if (typeof ans.content === "string") return ans.content;
      if (typeof ans.text === "string") return ans.text;
      if (typeof ans.summary === "string") return ans.summary;
      try {
        return JSON.stringify(ans);
      } catch (e) {
        return String(ans);
      }
    }
    return String(ans);
  };

  // Safe API base resolution (hardcoded to dev.api.medica.lk as requested)
  const getApiBase = () => {
    return "https://dev.api.medica.lk/api/v1";
  };

  const scrollToBottom = () => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("medica-chat-messages", JSON.stringify(messages));
    }
  }, [messages]);

  useEffect(() => {
    if (!isOpen) return;
    if (messages.length === 0) {
      setMessages([
        { from: "bot", text: "Hi! I'm Medica AI. How can I assist you today?" },
      ]);
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!input.trim() || isSending) return;
    setIsSending(true);
    const userText = input.trim();
    setMessages((prev) => [...prev, { from: "user", text: userText }]);
    setInput("");
    setLoading(true);

    const thankRegex = /^(thanks|thank you|thx|ty|thanku|thank\s?you)[.!]*$/i;
    if (thankRegex.test(userText)) {
      const reply = "You're welcome! Glad I could help. If you need anything else, ask.";
      setMessages((prev) => [...prev, { from: "bot", text: reply }]);
      setLoading(false);
      setIsSending(false);
      return;
    }

    try {
      const apiBase = getApiBase();
      const res = await fetch(`${apiBase}/openai/summarize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: userText }),
      });

      const json = await res.json();
      const rawAnswer = json?.data?.summary || json?.summary || json?.message || "Sorry, I don't have an answer for that.";
      const answer = normalizeAnswer(rawAnswer);

      setMessages((prev) => [...prev, { from: "bot", text: answer }]);
    } catch (err) {
      setMessages((prev) => [...prev, { from: "bot", text: "Sorry, something went wrong. Please try again later." }]);
      if (process.env.NODE_ENV === "development") console.error("Chatbot fetch error:", err);
    } finally {
      setLoading(false);
      setIsSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bottom-2 right-4 z-50 p-2 rounded-full bg-[#383838] text-white shadow-xl hover:scale-110 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-gray-500"
        aria-label="Open chatbot"
        style={{ boxShadow: "0 4px 24px 0 rgba(56,56,56,0.18)" }}
      >
        <span className="relative flex items-center justify-center">
          <BotMessageSquare className="w-7 h-7" aria-label="Chatbot icon" />
        </span>
      </button>

      {isOpen && (
        <div className="fixed bottom-24 right-6 w-[350px] max-w-[95vw] h-[420px] max-h-[80vh] bg-white/95 shadow-2xl border border-red-100 rounded-2xl flex flex-col z-[9999] animate-fade-in">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-600 bg-[#383838] rounded-t-2xl">
            <div className="flex items-center gap-2">
              <BotMessageSquare className="w-5 h-5 text-white" aria-label="Chatbot icon" />
              <h2 className="text-base font-semibold text-white">Medica AI Chatbot</h2>
            </div>
            <button onClick={() => setIsOpen(false)} aria-label="Close chatbot" className="p-1 transition rounded-full hover:bg-white/20">
              <X className="w-5 h-5 text-white" aria-label="Close icon" />
            </button>
          </div>

          <div ref={chatWindowRef} className="flex-1 px-3 py-2 space-y-2 overflow-y-auto bg-red-50/40 custom-scrollbar">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`max-w-[85%] break-words px-4 py-2 rounded-xl shadow-sm text-sm ${
                  msg.from === "user"
                    ? "bg-[#383838] text-white ml-auto mr-1 rounded-br-md"
                    : "bg-white text-gray-800 mr-auto ml-1 rounded-bl-md border border-gray-200"
                } animate-fade-in`}
                aria-live={msg.from === "bot" ? "polite" : undefined}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
              </div>
            ))}
            {loading && (
              <div className="max-w-[85%] px-4 py-2 rounded-xl bg-white text-gray-800 border border-red-100 flex items-center gap-2 animate-fade-in">
                <span className="w-4 h-4 border-2 border-red-400 rounded-full loader border-t-transparent animate-spin"></span>
              </div>
            )}
          </div>

          <form
            className="flex justify-between gap-2 px-4 py-3 bg-white border-t border-red-100 -b-2xl"
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            autoComplete="off"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="border w-[80%] border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#383838] bg-gray-50 placeholder:text-gray-400 transition"
              placeholder="Type your question here..."
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSend();
                }
              }}
              aria-label="Type your question here"
            />
            <button
              type="submit"
              className={`bg-[#383838] text-white px-3 py-2 rounded-lg hover:opacity-90 flex items-center justify-center shadow transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-gray-500 ${
                loading || !input.trim() || isSending ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={loading || !input.trim() || isSending}
              aria-label="Send message"
            >
              <Send className="w-4 h-4" aria-label="Send icon" />
            </button>
          </form>
        </div>
      )}

      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #383838;
          border-radius: 6px;
        }
        @keyframes animate-fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: animate-fade-in 0.3s;
        }
      `}</style>

      <style>{`
        .loader {
          display: inline-block;
          border-width: 2px;
          border-style: solid;
          border-radius: 9999px;
          border-color: #383838 #383838 #383838 transparent;
          animation: spin 0.7s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default Chatbot;
